import React, { useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';

import { Box, Stack, TextField, Typography } from '@mui/material';

import { UUID_REGEX } from '~/utils/isUUID';
import { toTitleCase } from '~/utils/toTitleCase';

interface TemplateSchema {
  properties: Record<
    string,
    {
      type: string;
      format?: string;
      minLength?: number;
      maxLength?: number;
      minimum?: number;
      maximum?: number;
      [key: string]: unknown;
    }
  >;
  required: Array<string>;
}

interface TemplateDataFormProps {
  schema: TemplateSchema;
  onDataChange: (data: Record<string, unknown>) => void;
  initialData?: Record<string, unknown>;
}

export default function TemplateDataForm({ schema, onDataChange, initialData = {} }: TemplateDataFormProps) {
  const {
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: initialData,
    mode: 'onChange',
  });

  useEffect(() => {
    const subscription = watch((value) => {
      const processedData = { ...value };

      Object.entries(schema.properties).forEach(([fieldName, fieldSchema]) => {
        if (
          fieldSchema.type === 'number' &&
          processedData[fieldName] !== undefined &&
          processedData[fieldName] !== ''
        ) {
          const numValue = Number(processedData[fieldName]);
          if (!isNaN(numValue)) {
            processedData[fieldName] = numValue;
          }
        }
      });

      onDataChange(processedData);
    });
    return () => subscription.unsubscribe();
  }, [watch, onDataChange, schema.properties]);

  const generateValidationRules = (fieldName: string, fieldSchema: TemplateSchema['properties'][string]) => {
    const rules: Record<string, unknown> = {};

    if (schema.required.includes(fieldName)) {
      rules.required = `${fieldName} is required`;
    }

    if (fieldSchema.type === 'string') {
      if (fieldSchema.format === 'uuid') {
        rules.pattern = {
          value: UUID_REGEX,
          message: `${fieldName} must be a valid UUID format`,
        };
      }

      if (fieldSchema.minLength) {
        rules.minLength = {
          value: fieldSchema.minLength,
          message: `${fieldName} must be at least ${fieldSchema.minLength} characters`,
        };
      }

      if (fieldSchema.maxLength) {
        rules.maxLength = {
          value: fieldSchema.maxLength,
          message: `${fieldName} must be no more than ${fieldSchema.maxLength} characters`,
        };
      }
    }

    if (fieldSchema.type === 'number') {
      rules.validate = {
        isNumber: (value: string) => {
          if (!value) {
            return true;
          }
          const num = Number(value);
          if (isNaN(num)) {
            return `${fieldName} must be a valid number`;
          }
          return true;
        },
        minimum: (value: string) => {
          if (!value || fieldSchema.minimum === undefined) {
            return true;
          }
          const num = Number(value);
          if (isNaN(num)) {
            return true;
          }
          if (num < fieldSchema.minimum) {
            return `${fieldName} must be at least ${fieldSchema.minimum}`;
          }
          return true;
        },
        maximum: (value: string) => {
          if (!value || fieldSchema.maximum === undefined) {
            return true;
          }
          const num = Number(value);
          if (isNaN(num)) {
            return true;
          }
          if (num > fieldSchema.maximum) {
            return `${fieldName} must be no more than ${fieldSchema.maximum}`;
          }
          return true;
        },
      };
    }

    return rules;
  };

  const generateFieldLabel = (fieldName: string, fieldSchema: TemplateSchema['properties'][string]) => {
    const isRequired = schema.required.includes(fieldName);
    let label = toTitleCase(fieldName);

    if (fieldSchema.format) {
      label += ` (${fieldSchema.format})`;
    } else if (fieldSchema.type === 'number') {
      label += ` (number)`;
    }

    return isRequired ? `${label} *` : label;
  };

  const generateHelperText = (fieldName: string, fieldSchema: TemplateSchema['properties'][string]) => {
    const parts = [];

    if (fieldSchema.type === 'string') {
      if (fieldSchema.minLength && fieldSchema.maxLength) {
        parts.push(`Length: ${fieldSchema.minLength}-${fieldSchema.maxLength} characters`);
      } else if (fieldSchema.minLength) {
        parts.push(`Min length: ${fieldSchema.minLength} characters`);
      } else if (fieldSchema.maxLength) {
        parts.push(`Max length: ${fieldSchema.maxLength} characters`);
      }

      if (fieldSchema.format === 'uuid') {
        parts.push('Must be a valid UUID');
      }
    }

    if (fieldSchema.type === 'number') {
      if (fieldSchema.minimum !== undefined && fieldSchema.maximum !== undefined) {
        parts.push(`Range: ${fieldSchema.minimum}-${fieldSchema.maximum}`);
      } else if (fieldSchema.minimum !== undefined) {
        parts.push(`Minimum: ${fieldSchema.minimum}`);
      } else if (fieldSchema.maximum !== undefined) {
        parts.push(`Maximum: ${fieldSchema.maximum}`);
      }
    }

    return parts.join(', ');
  };

  const generatePlaceholder = (fieldSchema: TemplateSchema['properties'][string]) => {
    if (fieldSchema.format === 'uuid') {
      return 'UUID format';
    }
    if (fieldSchema.type === 'number') {
      return 'Enter a number';
    }
    return undefined;
  };

  if (!schema?.properties) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          No schema properties found
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {Object.entries(schema.properties).map(([fieldName, fieldSchema]) => {
        const validationRules = generateValidationRules(fieldName, fieldSchema);
        const label = generateFieldLabel(fieldName, fieldSchema);
        const helperText = generateHelperText(fieldName, fieldSchema);
        const hasError = !!errors[fieldName];

        return (
          <Controller
            key={fieldName}
            name={fieldName}
            control={control}
            rules={validationRules}
            render={({ field }) => (
              <TextField
                {...field}
                label={label}
                fullWidth
                error={hasError}
                helperText={hasError ? errors[fieldName]?.message : helperText || undefined}
                placeholder={generatePlaceholder(fieldSchema)}
              />
            )}
          />
        );
      })}
    </Stack>
  );
}
